/**
 * Another app's exercise names, matched to this catalogue (`D201`).
 *
 * ── A SUGGESTION THE PERSON REVIEWS, NOT A DECISION ────────────────────────
 * "Bench Press (Barbell)" and "Barbell Bench Press" are one exercise and no
 * id says so, so the match is a judgement — made here, shown on the review
 * screen with the runners-up, and changed or skipped there before anything
 * is sent. Only a match at or above {@link AUTO_ACCEPT} arrives pre-selected;
 * below it the name arrives unmatched, which imports nothing for it unless
 * the person picks one.
 *
 * ── HOW THE SCORE IS MADE ──────────────────────────────────────────────────
 * Both names are normalised — accents folded, lower case, punctuation and
 * brackets to spaces, the usual abbreviations spelled out (`db`, `bb`, `kb`),
 * plural `s` dropped — and their words SORTED, so word order stops mattering.
 * The score is the Dice coefficient over the words, blended with the Dice
 * coefficient over character bigrams of the sorted text, which forgives a
 * spelling difference the word set cannot ("Pullup" against "Pull-up").
 * Every catalogue name is scored, English and the app's language both, once
 * per distinct source name: a few hundred names against ~3,000 exercises is
 * well under a second on a phone, and nothing leaves the device.
 */

export interface CatalogueName {
  readonly id: string;
  readonly name: string;
}

export interface MatchCandidate {
  readonly id: string;
  readonly name: string;
  readonly score: number;
}

/** Pre-selected at or above this; offered, unselected, below it. */
export const AUTO_ACCEPT = 0.72;

/** How many runners-up the review shows per name. */
export const CANDIDATES = 5;

const ABBREVIATIONS: Readonly<Record<string, string>> = {
  bb: "barbell",
  db: "dumbbell",
  dbs: "dumbbell",
  ez: "ez",
  kb: "kettlebell",
  smith: "smith",
};

const STOP_WORDS = new Set(["a", "the", "with", "de", "do", "da", "com", "e"]);

const COMBINING_MARKS = /[̀-ͯ]/g;
const NOT_WORD = /[^a-z0-9]+/g;
const PLURAL = /(?<=[a-z]{3})s$/;

export function normaliseName(name: string): string[] {
  return name
    .normalize("NFD")
    .replace(COMBINING_MARKS, "")
    .toLowerCase()
    .replace(NOT_WORD, " ")
    .trim()
    .split(" ")
    .filter((word) => word !== "" && !STOP_WORDS.has(word))
    .map((word) => ABBREVIATIONS[word] ?? word.replace(PLURAL, ""))
    .sort();
}

function dice<T>(a: readonly T[], b: readonly T[]): number {
  if (a.length === 0 || b.length === 0) {
    return 0;
  }
  const counts = new Map<T, number>();
  for (const item of a) {
    counts.set(item, (counts.get(item) ?? 0) + 1);
  }
  let shared = 0;
  for (const item of b) {
    const left = counts.get(item) ?? 0;
    if (left > 0) {
      shared += 1;
      counts.set(item, left - 1);
    }
  }
  return (2 * shared) / (a.length + b.length);
}

function bigrams(text: string): string[] {
  const compact = text.replaceAll(" ", "");
  const out: string[] = [];
  for (let index = 0; index < compact.length - 1; index += 1) {
    out.push(compact.slice(index, index + 2));
  }
  return out;
}

/** 0–1: how alike two names are, word order and spelling forgiven. */
export function similarity(a: string, b: string): number {
  const wordsA = normaliseName(a);
  const wordsB = normaliseName(b);
  if (wordsA.join(" ") === wordsB.join(" ") && wordsA.length > 0) {
    return 1;
  }
  return (
    0.6 * dice(wordsA, wordsB) +
    0.4 * dice(bigrams(wordsA.join(" ")), bigrams(wordsB.join(" ")))
  );
}

/**
 * The best {@link CANDIDATES} catalogue exercises for one source name, best
 * first, one entry per exercise id (its best-scoring name). `names` may hold
 * several names per id — English and the app's language.
 */
export function rankMatches(
  sourceName: string,
  names: readonly CatalogueName[]
): MatchCandidate[] {
  const best = new Map<string, MatchCandidate>();
  for (const entry of names) {
    const score = similarity(sourceName, entry.name);
    const held = best.get(entry.id);
    if (held === undefined || score > held.score) {
      best.set(entry.id, { id: entry.id, name: entry.name, score });
    }
  }
  return [...best.values()]
    .sort((a, b) => b.score - a.score || a.id.localeCompare(b.id))
    .slice(0, CANDIDATES);
}

/** The review's starting point: the best candidate when it clears the bar. */
export function autoChoice(
  candidates: readonly MatchCandidate[]
): string | null {
  const [first] = candidates;
  return first !== undefined && first.score >= AUTO_ACCEPT ? first.id : null;
}
